// Agent Marketing - Services data
import {
  Palette,
  Globe,
  MapPin,
  Share2,
  LayoutTemplate,
  Mail,
  Bot,
  Workflow,
  Printer,
  Search,
  Megaphone,
} from "lucide-react";

export const services = [
  {
    slug: "branding",
    icon: Palette,
    title: "Branding",
    tagline: "People trust what looks established.",
    short: "Logo, colours, typography, business cards, email signatures, and visual systems that make you look professional.",
    body: [
      "Branding is not decoration. It is what makes your business easier to recognize, remember, and refer.",
      "Agent Marketing builds a complete visual identity that stays consistent across every touchpoint: website, email, social, print, listings, and the physical materials clients see in their hands.",
    ],
    includes: [
      "Custom logo design",
      "Colour system",
      "Typography system",
      "Brand guide",
      "Business cards",
      "Email signatures",
      "Social banners",
      "Presentation materials",
      "Listing template alignment",
      "Print asset alignment",
    ],
    outcomes: [
      "You look established from the first impression",
      "Every asset feels like it came from the same business",
      "Clients recognize you across channels",
    ],
  },
  {
    slug: "websites",
    icon: Globe,
    title: "Websites",
    tagline: "Your website should make you easier to trust.",
    short: "Modern websites that help people understand who you are, what you do, and why they should contact you.",
    body: [
      "A good website does not need to be complicated. It needs to make the visitor feel confident.",
      "Agent Marketing builds websites that load fast, read clearly, and give prospects a reason to reach out before they leave.",
    ],
    includes: [
      "Professional design",
      "Mobile optimization",
      "Service pages",
      "Biography section",
      "Contact forms",
      "Listing support",
      "Lead capture",
      "SEO foundations",
      "Analytics",
      "Trust sections",
      "Booking links",
      "Integrations",
      "Ongoing management",
    ],
    outcomes: [
      "Prospects see a professional, ready-to-work business",
      "Every visit becomes a chance to capture a lead",
      "The site holds up next to larger competitors",
    ],
  },
  {
    slug: "google-business-profile",
    icon: MapPin,
    title: "Google Business Profile",
    tagline: "Your Google presence should help people trust you.",
    short: "Google Business Profile setup and optimization to help local clients find and trust you.",
    body: [
      "A complete Google Business Profile helps clients understand who you are, what you do, where you serve, and how to contact you.",
      "It is often the first result a prospect sees, and it should carry the same weight as your website.",
    ],
    includes: [
      "Profile setup",
      "Categories selection",
      "Service descriptions",
      "Photos and media",
      "Business information",
      "Review link setup",
      "Posting support",
      "Profile optimization",
      "Local visibility structure",
    ],
    outcomes: [
      "Local prospects find you first",
      "Your profile answers questions before they call",
      "Reviews accumulate and support trust",
    ],
  },
  {
    slug: "social-media",
    icon: Share2,
    title: "Social Media Assets",
    tagline: "Your social media should look like it belongs to the same business.",
    short: "Profile banners, branded posts, listing graphics, and content systems that keep your presence consistent.",
    body: [
      "Most professionals do not need random posts. They need consistency, presence, and content that reminds people they are active, credible, and available.",
    ],
    includes: [
      "Profile banners",
      "Branded post templates",
      "Educational assets",
      "Listing graphics",
      "Content calendars",
      "Captions",
      "Posting support",
      "Monthly content packages",
      "Campaign assets",
    ],
    outcomes: [
      "Feed feels active and intentional",
      "Referral partners see a consistent brand",
      "Content compounds instead of resetting every week",
    ],
  },
  {
    slug: "listing-templates",
    icon: LayoutTemplate,
    title: "Listing Templates",
    tagline: "Templates that make every property look like it came from you.",
    short: "Reusable listing marketing templates for social, websites, advertising, print, and client presentation.",
    body: [
      "AI can create a graphic. A brand creates recognition.",
      "Agent Marketing builds branded listing template systems that agents can reuse across listings, ads, websites, and social media so every property looks like it came from the same business.",
    ],
    includes: [
      "Just Listed template",
      "Just Sold template",
      "Open House template",
      "Price Improvement template",
      "For Lease template",
      "Business For Sale template",
      "Commercial Property template",
      "Featured Property template",
      "Market Update template",
      "Client Success template",
    ],
    outcomes: [
      "Every listing looks like it came from you",
      "Faster turnaround on new listings",
      "Consistent brand recognition across the market",
    ],
  },
  {
    slug: "business-email",
    icon: Mail,
    title: "Business Email",
    tagline: "Every message should support trust.",
    short: "Professional domain and email setup so every message supports trust.",
    body: [
      "A Gmail or Yahoo address quietly tells prospects you are not fully set up. A branded email address does the opposite.",
    ],
    includes: [
      "Domain registration support",
      "Professional email accounts",
      "Email signature design",
      "Deliverability setup",
      "Alias structure",
      "Team email accounts",
      "Ongoing email support",
    ],
    outcomes: [
      "Emails land in inboxes and feel professional",
      "Signatures reinforce the brand",
      "Every message looks like it came from an established business",
    ],
  },
  {
    slug: "ai-receptionist",
    icon: Bot,
    title: "AI Reception",
    tagline: "Stop losing leads when you are busy.",
    short: "AI receptionist and missed-call response systems that help protect opportunities when you are busy.",
    body: [
      "This does not replace the professional. It protects the opportunity until the professional can respond.",
    ],
    includes: [
      "AI receptionist setup",
      "Missed-call text back",
      "Lead intake",
      "Appointment routing",
      "FAQ answering",
      "SMS follow-up",
      "Email follow-up",
      "Lead notification systems",
    ],
    outcomes: [
      "Every inquiry gets an immediate response",
      "Fewer leads slip through the cracks",
      "You look responsive even during listing appointments",
    ],
  },
  {
    slug: "crm-automation",
    icon: Workflow,
    title: "CRM And Automation",
    tagline: "Turn inquiries into structured follow-up.",
    short: "Lead capture, routing, follow-up, review requests, and workflow support.",
    body: [
      "The difference between a busy professional and a growing business is usually the follow-up system.",
    ],
    includes: [
      "CRM setup",
      "Pipeline structure",
      "Form automation",
      "Lead intake",
      "Appointment routing",
      "Review request automation",
      "Email and SMS sequences",
      "Reporting dashboards",
    ],
    outcomes: [
      "No lead is forgotten",
      "Follow-up runs on rails",
      "Reviews build up automatically",
    ],
  },
  {
    slug: "print-physical",
    icon: Printer,
    title: "Print And Physical Assets",
    tagline: "Your brand should hold up offline too.",
    short: "Business cards, flyers, brochures, hats, apparel, presentation materials, and branded touchpoints.",
    body: [
      "Digital is not the whole business. A branded card, sign, or brochure still shapes how clients remember you after the meeting ends.",
    ],
    includes: [
      "Business cards",
      "Signs",
      "Flyers",
      "Brochures",
      "Presentation folders",
      "Letterhead",
      "Apparel",
      "Hats",
      "Promotional products",
      "Listing feature sheets",
      "Open house materials",
    ],
    outcomes: [
      "Every physical touchpoint reinforces the brand",
      "Clients keep and share your materials",
      "Offline recognition supports online reputation",
    ],
  },
  {
    slug: "seo-ai-visibility",
    icon: Search,
    title: "SEO And AI Visibility",
    tagline: "Make your business easier to find and recommend.",
    short: "Search and AI-friendly content structures that help your business become easier to find and recommend.",
    body: [
      "Search is no longer only Google. Prospects and AI tools both read your website to decide whether to recommend you.",
    ],
    includes: [
      "Semantic HTML structure",
      "Schema markup",
      "Service page architecture",
      "Local SEO",
      "Content strategy",
      "Internal linking",
      "AI-readable copy structure",
      "Ongoing content support",
    ],
    outcomes: [
      "Your business becomes easier to find",
      "AI tools recommend you more often",
      "Search traffic compounds over time",
    ],
  },
  {
    slug: "paid-advertising",
    icon: Megaphone,
    title: "Paid Advertising",
    tagline: "Reach the right people with intent.",
    short: "Paid campaigns on Google, Meta, and other platforms designed for agent-specific goals.",
    body: [
      "Paid ads work best when the presence they point to is already ready to earn trust. That is why we build the foundation first.",
    ],
    includes: [
      "Campaign strategy",
      "Audience research",
      "Ad creative",
      "Landing pages",
      "Tracking setup",
      "A/B testing",
      "Budget management",
      "Reporting",
    ],
    outcomes: [
      "Ad spend supports a system, not a guess",
      "Every visit is captured and followed up",
      "Campaigns can be measured and improved",
    ],
  },
];

export const findService = (slug) => services.find((s) => s.slug === slug);
